import React from "react"
import { Menu as MenuPrimitive } from "@base-ui/react/menu"
import cx from "classnames"

export type MenuItemProps = {
  /** Item content — compose with Menu.ItemIcon, Menu.ItemLabel and Menu.ItemDescription */
  children?: React.ReactNode
  /** Whether the item is disabled */
  disabled?: boolean
  /** Called when the item is clicked */
  onClick?: (event: React.MouseEvent) => void
  /** Replace the default element with your own */
  render?: React.ReactElement
  /** Whether this item represents a destructive action */
  destructive?: boolean
  /** Whether clicking the item closes the menu (default true). Set false for toggles. */
  closeOnClick?: boolean
}

export const itemStyles = [
  "grid grid-cols-[auto_1fr_auto] items-center gap-x-2 rounded-[10px] p-2 text-sm font-medium",
  "w-full cursor-pointer select-none",
  "text-gray-900 outline-none",
  "data-highlighted:bg-gray-900/[0.06]",
  "data-disabled:cursor-not-allowed data-disabled:opacity-50",
  // Gap and row-span only when a description is present to avoid phantom second row
  "[&:has([data-slot=menu-item-description])]:gap-y-1",
  // Slot placement — children are positioned by their data-slot attribute
  "[&_[data-slot=menu-item-icon]]:col-start-1 [&_[data-slot=menu-item-icon]]:row-start-1",
  "[&_[data-slot=menu-item-avatar]]:col-start-1 [&_[data-slot=menu-item-avatar]]:row-start-1",
  "[&_[data-slot=menu-item-label]]:col-start-2 [&_[data-slot=menu-item-label]]:row-start-1 [&_[data-slot=menu-item-label]]:min-w-0",
  "[&:has([data-slot=menu-item-description])_[data-slot=menu-item-label]]:font-semibold",
  "[&_[data-slot=menu-item-description]]:col-start-2 [&_[data-slot=menu-item-description]]:row-start-2",
  "[&_[data-slot=menu-item-trailing]]:col-start-3 [&_[data-slot=menu-item-trailing]]:row-start-1"
].join(" ")

const itemDestructiveStyles = "text-red-600 data-highlighted:bg-red-50"

export function MenuItem({
  children,
  disabled,
  onClick,
  render,
  destructive = false,
  closeOnClick
}: MenuItemProps) {
  return (
    <MenuPrimitive.Item
      data-slot="menu-item"
      render={render}
      disabled={disabled}
      closeOnClick={closeOnClick}
      className={cx(itemStyles, destructive && itemDestructiveStyles)}
      onClick={onClick}
    >
      {children}
    </MenuPrimitive.Item>
  )
}

MenuItem.displayName = "Menu.Item"
